import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ProceduresGridSkeleton } from '../index/_partials/ProcedureSkeletons';
import ArchivedProcedureCard from './_partials/ArchivedProcedureCard';
import { PageHeader } from '@/pages/_partials/mobile-headers';
import { useCachedApi } from '@/lib/hooks/use-cached-api';
import { useApiCache } from '@/lib/contexts/api-cache-context';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { BackButton } from '../../../components/ui/app/back-button';
import { useTranslation } from 'react-i18next';

/**
 * Génère une abréviation à partir du nom de la procédure
 * @param {string} name
 * @returns {string}
 */
function generateAbbreviation(name) {
    if (!name) return '??';
    const words = name.split(' ').filter((w) => w.length > 2);
    if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).charAt(0).toUpperCase() + name.substring(1, 2).toLowerCase();
}

/**
 * Transforme un template API en format attendu par ProcedureCard
 * @param {object} template
 * @returns {object}
 */
function transformTemplate(template) {
    return {
        id: template.uniq_id,
        uniq_id: template.uniq_id,
        name: template.name,
        color: template.color || 'rgb(74, 144, 226)',
        abbreviation: generateAbbreviation(template.name),
        description: template.description || '',
        deleted_at: template.deleted_at,
    };
}

export default function ProceduresArchives() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { invalidateCache } = useApiCache();

    // Récupération des templates archivés depuis l'API
    const {
        data: templates,
        isLoading,
        error,
        refetch,
    } = useCachedApi('procedure-templates-archives', async () => {
        const response = await horodatyApi.procedureTemplates.archives();
        console.log('ici', response);
        return response.data?.templates || [];
    });

    // Transformer les templates pour l'affichage
    const procedures = (templates || []).map(transformTemplate);

    const handleRestore = async (procedure) => {
        try {
            await horodatyApi.procedureTemplates.restore(procedure.uniq_id);
            toast.success(t('procedures:procedureRetablieAvecSucces'));
            invalidateCache('procedure-templates');
            refetch(true); // Rafraîchir la liste
        } catch (error) {
            console.error('Erreur lors du rétablissement de la procédure:', error);
            toast.error(t('procedures:erreurLorsDuRetablissementDeLaProcedure'));
        }
    };

    return (
        <>
            {/* Page Header */}
            <PageHeader title={t('procedures:proceduresArchivees')}>
                <BackButton onClick={() => navigate('/procedures')} />
            </PageHeader>

            <div className="p-8 pb-[120px] md:pb-8 space-y-6">
                {/* État de chargement */}
                {isLoading && <ProceduresGridSkeleton count={8} />}

                {/* État d'erreur */}
                {error && !isLoading && (
                    <div className="text-center py-12">
                        <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                        <p className="text-muted-foreground mb-4">{t('procedures:erreurLorsDuChargementDesProceduresArchivees')}</p>
                        {import.meta.env.DEV && (
                            <pre className="text-left bg-destructive/10 text-destructive p-4 rounded-lg mb-4 text-xs overflow-auto max-w-2xl mx-auto">
                                {error?.response?.data?.message || error?.message || JSON.stringify(error, null, 2)}
                            </pre>
                        )}
                        <Button variant="outline" onClick={() => refetch(true)}>
                            {t('procedures:reessayer')}
                        </Button>
                    </div>
                )}

                {/* Procedures Grid */}
                {!isLoading && !error && procedures.length === 0 && (
                    <div className="text-center py-12">
                        <p className="text-muted-foreground">{t('procedures:aucuneProcedureArchivee')}</p>
                    </div>
                )}

                {!isLoading && !error && procedures.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[15px]">
                        {procedures.map((procedure) => (
                            <ArchivedProcedureCard
                                key={procedure.uniq_id}
                                procedure={procedure}
                                onRestore={handleRestore}
                            />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
